import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { inboxApi } from "./api";
import { inboxKeys } from "./hooks";
import { useInboxToken } from "./useInboxToken";

const POLL_INTERVAL_MS = 15_000;
// The endpoint caps per_page at 100. A user with more unread threads than that
// gets badges for the 100 most recent only — a contact_ids filter on the
// communication-service side would lift that.
const PAGE_SIZE = 100;
const EMPTY = new Map<number, number>();

/**
 * Unread counts of the current user's conversations keyed by CRM contact id.
 * One polled request feeds every chat badge in a table (applications, leads)
 * instead of one by-contact lookup per row; cells subscribe to the same query,
 * so the request is shared no matter how many rows render.
 */
export const useUnreadByContact = (enabled = true) => {
  const { accessToken } = useInboxToken();

  const { data } = useQuery({
    queryKey: inboxKeys.unreadByContact(),
    queryFn: () =>
      inboxApi.listConversations({ unreads_only: true, per_page: PAGE_SIZE }),
    enabled: enabled && !!accessToken,
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
    retry: 1
  });

  return useMemo(() => {
    if (!data) return EMPTY;
    const byContact = new Map<number, number>();
    for (const conversation of data.conversations)
      byContact.set(
        conversation.contact_id,
        (byContact.get(conversation.contact_id) ?? 0) +
          conversation.unread_count
      );
    return byContact;
  }, [data]);
};
